/**
 * Shared logic for BulkReportsV2ConfigDialog. Controls the "Reports V2" nav
 * item via the properties/reports-v2-enabled + properties/reports-v2-permission-level
 * OrgConfig entries (see app/actg/src/config.js and packages/core/src/Feature.js).
 */

export const ENABLED_PROPERTY_ID = "properties/reports-v2-enabled";
export const PERMISSION_LEVEL_PROPERTY_ID =
    "properties/reports-v2-permission-level";

// Permission level mirrors the super admin level scale used throughout the
// admin UI (see SUPER_ADMIN_LEVELS in ./GrantSuperAdminDialog.js and
// SecurityUtils.SUPER_ADMIN_LEVEL_1..5 in packages/webutils/src/SecurityUtils.js).
// A user sees the "Reports V2" nav item only if their superAdminLevel is >=
// this value, so 0 is the least restrictive (visible to every org user) and
// 5 is the most restrictive (visible only to full administrators).
export const MIN_PERMISSION_LEVEL = 0;
export const MAX_PERMISSION_LEVEL = 5;
export const DEFAULT_PERMISSION_LEVEL = MIN_PERMISSION_LEVEL;

// For reference/documentation only (e.g. tooltips) - what each level means:
export const PERMISSION_LEVEL_DESCRIPTIONS = [
    {
        value: 0,
        label: "None",
        description: "Visible to every user in the org (no restriction)."
    },
    {
        value: 1,
        label: "Basic Support Access",
        description: "Visible to Level 1 super admins (support) and above."
    },
    {
        value: 2,
        label: "Advanced Support Access",
        description:
            "Visible to Level 2 super admins (advanced support) and above."
    },
    {
        value: 3,
        label: "Implementation Access",
        description:
            "Visible to Level 3 super admins (implementation) and above."
    },
    {
        value: 4,
        label: "Engineering / PM Access",
        description:
            "Visible to Level 4 super admins (engineering/PM) and above."
    },
    {
        value: 5,
        label: "Administrator / Full Access",
        description: "Visible only to Level 5 super admins (administrators)."
    }
];

/**
 * Clamps a permission level to the valid [MIN_PERMISSION_LEVEL, MAX_PERMISSION_LEVEL]
 * range, coercing non-numeric input to MIN_PERMISSION_LEVEL.
 * @param {number|string} level
 * @returns {number}
 */
export function clampPermissionLevel(level) {
    const parsed = parseInt(level, 10);
    if (isNaN(parsed)) {
        return MIN_PERMISSION_LEVEL;
    }
    return Math.min(
        MAX_PERMISSION_LEVEL,
        Math.max(MIN_PERMISSION_LEVEL, parsed)
    );
}

/**
 * @param {boolean} enabled
 * @param {number} permissionLevel
 * @returns {Array<{id: string, value: *}>} OrgConfig entries to send to putOrgConfigurations
 */
export function buildReportsV2Configs(enabled, permissionLevel) {
    const configs = [{ id: ENABLED_PROPERTY_ID, value: enabled }];

    if (enabled) {
        configs.push({
            id: PERMISSION_LEVEL_PROPERTY_ID,
            value: clampPermissionLevel(permissionLevel)
        });
    }

    return configs;
}

/**
 * Parses org/tenant IDs from free text (one per line, or comma/space
 * separated), trimming whitespace, dropping empty entries and de-duping.
 * @param {string} text
 * @returns {string[]}
 */
export function parseOrgIds(text) {
    const ids = (text || "")
        .split(/[,\s\n]+/)
        .map((id) => id.trim())
        .filter(Boolean);

    return Array.from(new Set(ids));
}

// BulkReportsV2ConfigDialog lets an admin pick exactly one way to target
// orgs: a free-text list of org/tenant IDs, a single merchant ID (every
// tenant under that merchant), or a stage (every tenant currently in that
// stage, same values as the "Create Merchant" Stage dropdown in ./Config.js).
export const TARGET_MODES = {
    ORG_IDS: "orgIds",
    MERCHANT_ID: "merchantId",
    STAGE: "stage"
};

/**
 * Extracts org/tenant ids for a given merchantId out of the `organizations`
 * map returned by getAllOrganizations (rest/API.js), which is keyed by
 * merchantId -> Org[].
 * @param {Object.<string, Array<{id: string|number, merchantId: string}>>} organizations
 * @param {string} merchantId
 * @returns {string[]}
 */
export function extractOrgIdsForMerchant(organizations, merchantId) {
    if (!organizations || !merchantId) {
        return [];
    }

    const target = String(merchantId).trim();
    const exactMatch = organizations[target];
    if (exactMatch) {
        return Array.from(new Set(exactMatch.map((org) => String(org.id))));
    }

    // Fallback in case the response groups by a differently-cased/whitespaced
    // key than our input: scan every org's own merchantId field instead.
    const ids = [];
    Object.values(organizations).forEach((orgs) => {
        (orgs || []).forEach((org) => {
            if (org.merchantId && String(org.merchantId).trim() === target) {
                ids.push(String(org.id));
            }
        });
    });

    return Array.from(new Set(ids));
}

/**
 * Extracts org/tenant ids for every org whose `stage` matches, out of the
 * `organizations` map returned by getAllOrganizations (rest/API.js).
 * @param {Object.<string, Array<{id: string|number, stage: string}>>} organizations
 * @param {string} stage
 * @returns {string[]}
 */
export function extractOrgIdsForStage(organizations, stage) {
    if (!organizations || !stage) {
        return [];
    }

    const ids = [];
    Object.values(organizations).forEach((orgs) => {
        (orgs || []).forEach((org) => {
            if (org.stage === stage) {
                ids.push(String(org.id));
            }
        });
    });

    return Array.from(new Set(ids));
}
